import { useState } from "react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { useDebateVerdict } from "@/sdk/hooks/use-alpha";

export function OraclePanel() {
  const [marketId, setMarketId] = useState("");
  const [queried, setQueried] = useState("");
  const verdict = useDebateVerdict(queried, Boolean(queried));
  const debate = verdict.data?.debates?.[0];
  const value = debate ? {
    probability: debate.judge?.final_probability ?? debate.consensus_probability ?? null,
    reasoning: debate.judge?.rationale ?? null,
    votes: debate.blind_votes ?? [],
  } : null;
  // A debate row can exist with BOTH judge.final_probability and
  // consensus_probability null — that is "no verdict", never 0% / SELL.
  const hasVerdict = value !== null && value.probability !== null;

  return (
    <Panel title="Oracle - Debate Council Verdict">
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none"
            placeholder="Market ID..."
            value={marketId}
            onChange={(event) => setMarketId(event.target.value)}
          />
          <button type="button" onClick={() => setQueried(marketId)} className="rounded bg-purple-600 px-3 py-1.5 text-xs text-white hover:bg-purple-500">
            Consult Oracle
          </button>
        </div>
        {verdict.isLoading ? <LoadingState text="Consulting debate council..." /> : null}
        {verdict.isError ? (
          <QueryErrorState what="debate verdict" onRetry={() => verdict.refetch()} />
        ) : null}
        {Boolean(queried) && verdict.isSuccess && !debate ? (
          <p className="py-4 text-center text-sm text-white/40">No debate found for this market.</p>
        ) : null}
        {value ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-white/40">Verdict</span>
              <div className="flex items-center gap-3">
                {hasVerdict ? (
                  <>
                    <span className={`text-3xl font-bold ${(value.probability ?? 0) >= 0.5 ? "text-green-400" : "text-red-400"}`}>
                      {Math.round((value.probability ?? 0) * 100)}%
                    </span>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${(value.probability ?? 0) >= 0.5 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                      {(value.probability ?? 0) >= 0.5 ? "BUY bias" : "SELL bias"}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-bold text-white/40">n/a</span>
                    <span className="rounded bg-white/10 px-2 py-0.5 text-xs font-semibold text-white/50">
                      no verdict
                    </span>
                  </>
                )}
              </div>
            </div>
            {value.reasoning ? <p className="border-l-2 border-purple-500/40 pl-3 text-sm leading-relaxed text-white/70">{value.reasoning}</p> : null}
            {value.votes.length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-xs uppercase tracking-wide text-white/40">Agent Votes</p>
                {value.votes.map((vote, index) => (
                  <div key={index} className="flex items-center justify-between rounded bg-white/5 px-2 py-1.5 text-xs">
                    <span className="font-medium uppercase text-white/60">{vote.model ?? vote.agent_id}</span>
                    {typeof vote.probability === "number" ? (
                      <span className={`font-bold ${vote.probability >= 0.5 ? "text-green-400" : "text-red-400"}`}>
                        {Math.round(vote.probability * 100)}%
                      </span>
                    ) : (
                      <span className="font-bold text-white/40">n/a</span>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
